import { Suspense, lazy } from 'react';
import { Navigate, useRoutes, useLocation } from 'react-router-dom';
import { PERMISSIONS } from '../guards/Permissions';
// layouts
import MainLayout from '../layouts/main';
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
// guards
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';
import RoleBasedGuard from '../guards/RoleBasedGuard';
// components
import LoadingScreen from '../components/LoadingScreen';

// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();
  const isDashboard = pathname.includes('/dashboard');

  return (
    <Suspense
      fallback={
        <LoadingScreen
          sx={{
            ...(!isDashboard && {
              top: 0,
              left: 0,
              width: 1,
              zIndex: 9999,
              position: 'fixed'
            })
          }}
        />
      }
    >
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  return useRoutes([
    {
      path: 'auth',
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          )
        }
      ]
    },

    // Dashboard Routes
    {
      path: 'dashboard',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to="/dashboard/app" replace />, index: true },
        { path: 'app', element: <GeneralApp /> },
        {
          path: 'rapports',
          children: [
            { element: <Navigate to="/dashboard/rapports/root" replace />, index: true },
            {
              path: 'root',
              element: (
                <RoleBasedGuard permission={PERMISSIONS.VIEW_REPORT_VENTE}>
                  <Rapport />
                </RoleBasedGuard>
              )
            },
            {
              path: 'tirage',
              element: (
                <RoleBasedGuard permission={PERMISSIONS.VIEW_REPORT_PARTIEL}>
                  <RapportApresVente />
                </RoleBasedGuard>
              )
            },
            {
              path: 'avantTirage',
              element: (
                <RoleBasedGuard permission={PERMISSIONS.VIEW_REPORT_PARTIEL}>
                  <RapportAvantVente />
                </RoleBasedGuard>
              )
            },
            
            {
              path: 'journalier',
              element: (
                <RoleBasedGuard permission={PERMISSIONS.VIEW_REPORT_PARTIEL}>
                  <RapportJournalier />
                </RoleBasedGuard>
              )
            },
            {
              path: 'agent',
              element: (
                <RoleBasedGuard permission={PERMISSIONS.VIEW_REPORT_AGENT}>
                  <RapportAgent />
                </RoleBasedGuard>
              )
            }
          ]
        },
        {
          path: 'transactions',
          children: [
            { element: <Navigate to="/dashboard/transactions/root" replace />, index: true },
            {
              path: 'root',
              element: (
                <RoleBasedGuard permission="view_wallettransaction">
                  <Transaction />
                </RoleBasedGuard>
              )
            },
            {
              path: 'new',
              element: (
                <RoleBasedGuard permission="add_wallettransaction">
                  <TransactionCreate />
                </RoleBasedGuard>
              )
            }
          ]
        },
        {
          path: 'borlette',
          children: [
            { element: <Navigate to="/dashboard/borlette/list-fiches" replace />, index: true },
            {
              path: 'blocage-boule',
              element: (
                <RoleBasedGuard permission="view_bouleblock">
                  <Boule />
                </RoleBasedGuard>
              )
            },
            {
              path: 'blocage-boule/new',
              element: (
                <RoleBasedGuard permission="add_bouleblock">
                  <BouleCreate />
                </RoleBasedGuard>
              )
            },
            {
              path: 'blocage-boule/delete',
              element: (
                <RoleBasedGuard permission="add_bouleblock">
                  <BouleDelCreate />
                </RoleBasedGuard>
              )
            },
            {
              path: 'statistiques',
              element: (
                <RoleBasedGuard permission={PERMISSIONS.VIEW_STATISTIQUE}>
                  <Statistique />
                </RoleBasedGuard>
              )
            },
            {
              path: 'lotto',
              element: (
                <RoleBasedGuard permission="view_lotto">
                  <Lotto />
                </RoleBasedGuard>
              )
            },
            {
              path: 'lotto/new',
              element: (
                <RoleBasedGuard permission="add_lotto">
                  <LottoCreate />
                </RoleBasedGuard>
              )
            },
            {
              path: 'lotto/:optionId/edit',
              element: (
                <RoleBasedGuard permission="change_lotto">
                  <LottoCreate />
                </RoleBasedGuard>
              )
            },
            {
              path: 'list-fiches',
              element: (
                <RoleBasedGuard permission="view_fichevente">
                  <Fiche />
                </RoleBasedGuard>
              )
            },
            
            {
              path: 'list-fiches-gagnants',
              element: (
                <RoleBasedGuard permission={PERMISSIONS.VIEW_FICHE_WIN}>
                  <FicheGagnant />
                </RoleBasedGuard>
              )
            },
            {
              path: 'list-fiches-supprimer',
              element: (
                <RoleBasedGuard permission={PERMISSIONS.VIEW_FICHE_DELETE}>
                  <FicheSupp />
                </RoleBasedGuard>
              )
            }
          ]
        },
        {
          path: 'borlette-configuration',
          children: [
            { element: <Navigate to="/dashboard/borlette-configuration/limite-game" replace />, index: true },
            {
              path: 'limite-intervalle-option',
              element: (
                <RoleBasedGuard permission="view_intervalleoption">
                  <IntervalleOption />
                </RoleBasedGuard>
              )
            },
            {
              path: 'limite-intervalle-option/new',
              element: (
                <RoleBasedGuard permission="add_intervalleoption">
                  <IntervalleOptionCreate />
                </RoleBasedGuard>
              )
            },
            {
              path: 'limite-intervalle-option/:intervalleOptionId/edit',
              element: (
                <RoleBasedGuard permission="change_intervalleoption">
                  <IntervalleOptionCreate />
                </RoleBasedGuard>
              )
            },
            {
              path: 'limite-game',
              element: (
                <RoleBasedGuard permission="view_typegame">
                  <TypeGame />
                </RoleBasedGuard>
              )
            },
            {
              path: 'limite-game/new',
              element: (
                <RoleBasedGuard permission="add_typegame">
                  <TypeGameCreate />
                </RoleBasedGuard>
              )
            },
            {
              path: 'limite-game/:typeGameId/edit',
              element: (
                <RoleBasedGuard permission="change_typegame">
                  <TypeGameCreate />
                </RoleBasedGuard>
              )
            },
            {
              path: 'limite-game-per-agent',
              element: (
                <RoleBasedGuard permission="view_typegameagent">
                  <TypeGameAgent />
                </RoleBasedGuard>
              )
            },
            {
              path: 'limite-game-per-agent/new',
              element: (
                <RoleBasedGuard permission="add_typegameagent">
                  <TypeGameAgentCreate />
                </RoleBasedGuard>
              )
            },
            {
              path: 'limite-game-per-agent/:typeGameAgentId/edit',
              element: (
                <RoleBasedGuard permission="change_typegameagent">
                  <TypeGameAgentCreate />
                </RoleBasedGuard>
              )
            },

            // ///////////////////////////////
             {
              path: 'limite-game-per-surcussale',
              element: (
                <RoleBasedGuard permission="view_typegameagent">
                  <TypeGameSurcussale />
                </RoleBasedGuard>
              )
            },
            {
              path: 'limite-game-per-surcussale/new',
              element: (
                <RoleBasedGuard permission="add_typegameagent">
                  <TypeGameSurcussaleCreate />
                </RoleBasedGuard>
              )
            },
            {
              path: 'limite-game-per-surcussale/:typeGameAgentId/edit',
              element: (
                <RoleBasedGuard permission="change_typegameagent">
                  <TypeGameSurcussaleCreate />
                </RoleBasedGuard>
              )
            },
           
            // ////////////////////////////
            {
              path: 'limite-boule',
              element: (
                <RoleBasedGuard permission="view_typeboule">
                  <TypeBoule />
                </RoleBasedGuard>
              )
            },
            {
              path: 'limite-boule/new',
              element: (
                <RoleBasedGuard permission="add_typeboule">
                  <TypeBouleCreate />
                </RoleBasedGuard>
              )
            },
            {
              path: 'limite-boule/:typeBouleId/edit',
              element: (
                <RoleBasedGuard permission="change_typeboule">
                  <TypeBouleCreate />
                </RoleBasedGuard>
              )
            },
            {
              path: 'limite-boule-per-agent',
              element: (
                <RoleBasedGuard permission="view_typebouleagent">
                  <TypeBouleAgent />
                </RoleBasedGuard>
              )
            },
            {
              path: 'limite-boule-per-agent/new',
              element: (
                <RoleBasedGuard permission="add_typebouleagent">
                  <TypeBouleAgentCreate />
                </RoleBasedGuard>
              )
            },
            {
              path: 'limite-boule-per-agent/:typeBouleAgentId/edit',
              element: (
                <RoleBasedGuard permission="change_typebouleagent">
                  <TypeBouleAgentCreate />
                </RoleBasedGuard>
              )
            }
          ]
        },
        {
          path: 'limit-configuration',
          children: [
            { element: <Navigate to="/dashboard/limit-configuration/100-boules" replace />, index: true },
            {
              path: '100-boules',
              element: (
                <RoleBasedGuard permission="change_limiteallboules">
                  <LimitAllBouleCreate />
                </RoleBasedGuard>
              )
            },
            {
              path: '100-boules/list',
              element: (
                <RoleBasedGuard permission="view_limiteallboules">
                  <LimitAllBoule />
                </RoleBasedGuard>
              )
            },
            {
              path: '100-boules/:limitId/edit',
              element: (
                <RoleBasedGuard permission="change_limiteallboules">
                  <LimitAllBouleCreate />
                </RoleBasedGuard>
              )
            },

             {
              path: 'boules-grappe',
              element: (
                <RoleBasedGuard permission="view_typegame">
                  <LimiteGrappeCreate />
                </RoleBasedGuard>
              )
            },

            {
              path: 'boules-grappe/list',
              element: (
                <RoleBasedGuard permission="view_typegame">
                  <LimiteGrappe />
                </RoleBasedGuard>
              )
            },

            {
              path: 'boules-grappe/:limitId/edit',
              element: (
                <RoleBasedGuard permission="view_typegame">
                  <LimiteGrappeCreate />
                </RoleBasedGuard>
              )
            },

            {
              path: 'boules-paires',
              element: (
                <RoleBasedGuard permission="change_limiteboulepaire">
                  <LimitBoulePaireCreate />
                </RoleBasedGuard>
              )
            },
            {
              path: 'boules-paires/list',
              element: (
                <RoleBasedGuard permission="view_limiteboulepaire">
                  <LimitBoulePaire />
                </RoleBasedGuard>
              )
            },
            {
              path: 'boules-paires/:limitId/edit',
              element: (
                <RoleBasedGuard permission="change_limiteboulepaire">
                  <LimitBoulePaireCreate />
                </RoleBasedGuard>
              )
            },

           
         

            {
              path: 'boules-points',
              element: (
                <RoleBasedGuard permission="change_limitepointsboules">
                  <LimitBoulePointCreate />
                </RoleBasedGuard>
              )
            },
            {
              path: 'boules-points/list',
              element: (
                <RoleBasedGuard permission="view_limitepointsboules">
                  <LimitBoulePoint />
                </RoleBasedGuard>
              )
            },
            {
              path: 'boules-points/:limitId/edit',
              element: (
                <RoleBasedGuard permission="change_limitepointsboules">
                  <LimitBoulePointCreate />
                </RoleBasedGuard>
              )
            },
          ]
        },
        {
          path: 'tirage',
          children: [
            { element: <Navigate to="/dashboard/tirage/list" replace />, index: true },
            {
              path: 'list',
              element: (
                <RoleBasedGuard permission="view_tirage">
                  <Tirage />
                </RoleBasedGuard>
              )
            },
            {
              path: 'new',
              element: (
                <RoleBasedGuard permission="add_tirage">
                  <TirageCreate />
                </RoleBasedGuard>
              )
            },
            {
              path: 'automatique',
              element: (
                <RoleBasedGuard permission="add_tirage_auto">
                  <TirageAuto />
                 </RoleBasedGuard>
              )
            },
            
            {
              path: 'remove',
              element: (
                <RoleBasedGuard permission="add_tirage_auto">
                  <RemoveTirageAuto />
                 </RoleBasedGuard>
              )
            },
            {
              path: ':tirageId/edit',
              element: (
                <RoleBasedGuard permission="change_tirage">
                  <TirageCreate />
                </RoleBasedGuard>
              )
            },
            {
              path: 'configuration/generale',
              element: (
                <RoleBasedGuard permission="view_tauxtirage">
                  <TirageConfCreate />
                </RoleBasedGuard>
              )
            },
            {
              path: 'configuration/generale/:primeId/edit',
              element: (
                <RoleBasedGuard permission="change_tauxtirage">
                  <TirageConfCreate />
                </RoleBasedGuard>
              )
            },
            {
              path: 'configuration/generale/list',
              element: (
                <RoleBasedGuard permission="view_tauxtirage">
                  <PrimeGenerale />
                </RoleBasedGuard>
              )
            },
            {
              path: 'configuration/:type',
              element: (
                <RoleBasedGuard
                  permission={'view_agenttauxtirage' || 'view_tiragetauxtirage' || 'view_surcussaletauxtirage'}
                >
                  <Prime />
                </RoleBasedGuard>
              )
            },
            {
              path: 'configuration/:type/:configurationId/edit',
              element: (
                <RoleBasedGuard
                  permission={'change_agenttauxtirage' || 'change_tiragetauxtirage' || 'change_surcussaletauxtirage'}
                >
                  <TirageConfCreateModif />
                </RoleBasedGuard>
              )
            },
            {
              path: 'configuration/:type/new',
              element: (
                <RoleBasedGuard
                  permission={'add_agenttauxtirage' || 'add_tiragetauxtirage' || 'add_surcussaletauxtirage'}
                >
                  <TirageConfCreateModif />
                </RoleBasedGuard>
              )
            },
            {
              path: 'infotirage',
              element: (
                <RoleBasedGuard permission="view_infotirage">
                  <InfoTirage />
                </RoleBasedGuard>
              )
            },
            {
              path: 'infotirage/new',
              element: (
                <RoleBasedGuard permission="add_infotirage">
                  <InfoTirageCreate />
                </RoleBasedGuard>
              )
            },
            {
              path: 'infotirage/:infoTirageId/edit',
              element: (
                <RoleBasedGuard permission="change_infotirage">
                  <InfoTirageCreate />
                </RoleBasedGuard>
              )
            }
          ]
        },

        {
          path: 'tirageStatus2',
          children: [
            { element: <Navigate to="/dashboard/tirageStatus2/list" replace />, index: true },
            {
              path: 'list',
              element: (
                <RoleBasedGuard permission="add_tirage">
                  <TirageStatus2 />
                </RoleBasedGuard>
              )
            },
          ]},
        
         {
          path: 'tirageStatus',
          children: [
            { element: <Navigate to="/dashboard/tirageStatus/list" replace />, index: true },
            {
              path: 'list',
              element: (
                <RoleBasedGuard permission="add_tirage">
                  <TirageStatus />
                </RoleBasedGuard>
              )
            },
            {
              path: 'new',
              element: (
                <RoleBasedGuard permission="add_tirage">
                  <TirageCreate />
                </RoleBasedGuard>
              )
            },
            {
              path: 'automatique',
              element: (
                // <RoleBasedGuard permission="add_tirage">
                  <TirageAuto />
                // </RoleBasedGuard>
              )
            },

           
            {
              path: ':tirageId/edit',
              element: (
                <RoleBasedGuard permission="change_tirage">
                  <TirageCreate />
                </RoleBasedGuard>
              )
            },
            {
              path: 'configuration/generale',
              element: (
                <RoleBasedGuard permission="view_tauxtirage">
                  <TirageConfCreate />
                </RoleBasedGuard>
              )
            },
            {
              path: 'configuration/generale/:primeId/edit',
              element: (
                <RoleBasedGuard permission="change_tauxtirage">
                  <TirageConfCreate />
                </RoleBasedGuard>
              )
            },
            {
              path: 'configuration/generale/list',
              element: (
                <RoleBasedGuard permission="view_tauxtirage">
                  <PrimeGenerale />
                </RoleBasedGuard>
              )
            },
            {
              path: 'configuration/:type',
              element: (
                <RoleBasedGuard
                  permission={'view_agenttauxtirage' || 'view_tiragetauxtirage' || 'view_surcussaletauxtirage'}
                >
                  <Prime />
                </RoleBasedGuard>
              )
            },
            {
              path: 'configuration/:type/:configurationId/edit',
              element: (
                <RoleBasedGuard
                  permission={'change_agenttauxtirage' || 'change_tiragetauxtirage' || 'change_surcussaletauxtirage'}
                >
                  <TirageConfCreateModif />
                </RoleBasedGuard>
              )
            },
            {
              path: 'configuration/:type/new',
              element: (
                <RoleBasedGuard
                  permission={'add_agenttauxtirage' || 'add_tiragetauxtirage' || 'add_surcussaletauxtirage'}
                >
                  <TirageConfCreateModif />
                </RoleBasedGuard>
              )
            },
            {
              path: 'infotirage',
              element: (
                <RoleBasedGuard permission="view_infotirage">
                  <InfoTirage />
                </RoleBasedGuard>
              )
            },
            {
              path: 'infotirage/new',
              element: (
                <RoleBasedGuard permission="add_infotirage">
                  <InfoTirageCreate />
                </RoleBasedGuard>
              )
            },
            {
              path: 'infotirage/:infoTirageId/edit',
              element: (
                <RoleBasedGuard permission="change_infotirage">
                  <InfoTirageCreate />
                </RoleBasedGuard>
              )
            }
          ]
        },



        {
          path: 'user',
          children: [
            { element: <Navigate to="/dashboard/user/account" replace />, index: true },
            {
              path: 'account',
              element: <UserAccount />
            },
            {
              path: 'tracabilite-agent',
              element: (
                <RoleBasedGuard permission="view_traceagentaction">
                  <TracabiliteAgent />
                </RoleBasedGuard>
              )
            },
            {
              path: 'tracabilite-company',
              element: (
                <RoleBasedGuard permission="view_tracecompanyaction">
                  <TracabiliteCompany />
                </RoleBasedGuard>
              )
            },
            {
              path: 'tracabilite-supervisor',
              element: (
                <RoleBasedGuard permission="view_tracesupervisoraction">
                  <TracabiliteSupervisor />
                </RoleBasedGuard>
              )
            },
            {
              path: 'generate-agent',
              element: (
                <RoleBasedGuard permission="add_agent">
                  <GenerateAgent />
                </RoleBasedGuard>
              )
            },
            {
              path: 'generate-company',
              element: (
                <RoleBasedGuard permission="add_agent">
                  <GenerateCompany />
                </RoleBasedGuard>
              )
            },
             {
              path: 'generate-tirage',
              element: (
                <RoleBasedGuard permission="add_agent">
                  <GenerateTirage />
                </RoleBasedGuard>
              )
            },
            {
              path: 'message-push',
              element: (
                <RoleBasedGuard permission="view_apppromote">
                  <AppPromote />
                </RoleBasedGuard>
              )
            },
            {
              path: 'message-push/new',
              element: (
                <RoleBasedGuard permission="add_apppromote">
                  <AppPromoteCreate />
                </RoleBasedGuard>
              )
            },
            {
              path: 'message-push/:messageId/edit',
              element: (
                <RoleBasedGuard permission="change_apppromote">
                  <AppPromoteCreate />
                </RoleBasedGuard>
              )
            },
            {
              path: 'pays',
              element: (
                <RoleBasedGuard permission="view_country">
                  <Pays />
                </RoleBasedGuard>
              )
            },
            {
              path: 'pays/new',
              element: (
                <RoleBasedGuard permission="add_country">
                  <PaysCreate />
                </RoleBasedGuard>
              )
            },
            {
              path: 'pays/:paysId/edit',
              element: (
                <RoleBasedGuard permission="change_country">
                  <PaysCreate />
                </RoleBasedGuard>
              )
            }
          ]
        },
        {
          path: 'agent',
          children: [
            { element: <Navigate to="/dashboard/agent/list" replace />, index: true },
            {
              path: 'list',
              element: (
                <RoleBasedGuard permission="view_agent">
                  <Agent />
                </RoleBasedGuard>
              )
            },
            {
              path: 'new',
              element: (
                <RoleBasedGuard permission="add_agent">
                  <AgentCreate />
                </RoleBasedGuard>
              )
            },
            {
              path: ':agentId/edit',
              element: (
                <RoleBasedGuard permission="change_agent">
                  <AgentCreate />
                </RoleBasedGuard>
              )
            }
          ]
        },
        {
          path: 'delete_fiche',
          children: [
            { element: <Navigate to="/dashboard/delete_fiche/list" replace />, index: true },
            {
              path: 'list',
              element: (
                <RoleBasedGuard permission="add_wallettransaction">
                  <DeleteFicheRange />
                </RoleBasedGuard>
              )
            }
          ]
        },
       
        {
          path: 'supervisor',
          children: [
            { element: <Navigate to="/dashboard/supervisor/list" replace />, index: true },
            {
              path: 'list',
              element: (
                <RoleBasedGuard permission="view_supervisor">
                  <Supervisor />
                </RoleBasedGuard>
              )
            },
            {
              path: 'new',
              element: (
                <RoleBasedGuard permission="add_supervisor">
                  <SupervisorCreate />
                </RoleBasedGuard>
              )
            },
            {
              path: ':supervisorId/edit',
              element: (
                <RoleBasedGuard permission="change_supervisor">
                  <SupervisorCreate />
                </RoleBasedGuard>
              )
            }
          ]
        },

        {
          path: 'mariage',
          children: [
            { element: <Navigate to="/dashboard/mariage/list" replace />, index: true },
            {
              path: 'list',
              element: (
                <RoleBasedGuard permission="view_supervisor">
                  <MariageAgent />
                </RoleBasedGuard>
              )
            },
            {
              path: 'new',
              element: (
                <RoleBasedGuard permission="add_supervisor">
                  <MariageCreate />
                </RoleBasedGuard>
              )
            },
            {
              path: ':mariageId/edit',
              element: (
                <RoleBasedGuard permission="change_supervisor">
                  <SupervisorCreate />
                </RoleBasedGuard>
              )
            }
          ]
        },

        {
          path: 'mariageSurcussale',
          children: [
            { element: <Navigate to="/dashboard/mariageSurcussale/list" replace />, index: true },
            {
              path: 'list',
              element: (
                <RoleBasedGuard permission="view_supervisor">
                  <MariageSurcussale />
                </RoleBasedGuard>
              )
            },
            {
              path: 'new',
              element: (
                <RoleBasedGuard permission="add_supervisor">
                  <MariageSurCreate />
                </RoleBasedGuard>
              )
            },
            {
              path: ':mariageId/edit',
              element: (
                <RoleBasedGuard permission="change_supervisor">
                  <SupervisorCreate />
                </RoleBasedGuard>
              )
            }
          ]
        },

        {
          path: 'amountmariageSurcussale',
          children: [
            { element: <Navigate to="/dashboard/amountmariageSurcussale/list" replace />, index: true },
            {
              path: 'list',
              element: (
                <RoleBasedGuard permission="view_supervisor">
                  <AmountMariageSurcussale />
                </RoleBasedGuard>
              )
            },
            {
              path: 'new',
              element: (
                <RoleBasedGuard permission="add_supervisor">
                  <MariageSurAMountCreate />
                </RoleBasedGuard>
              )
            },
            {
              path: ':mariageId/edit',
              element: (
                <RoleBasedGuard permission="change_supervisor">
                  <SupervisorCreate />
                </RoleBasedGuard>
              )
            }
          ]
        },


        {
          path: 'amountmariageAgent',
          children: [
            { element: <Navigate to="/dashboard/amountmariageAgent/list" replace />, index: true },
            {
              path: 'list',
              element: (
                <RoleBasedGuard permission="view_supervisor">
                  <AmountMariageAgent />
                </RoleBasedGuard>
              )
            },
            {
              path: 'new',
              element: (
                <RoleBasedGuard permission="add_supervisor">
                  <MariageAgentAMountCreate />
                </RoleBasedGuard>
              )
            },
            {
              path: ':mariageId/edit',
              element: (
                <RoleBasedGuard permission="change_supervisor">
                  <SupervisorCreate />
                </RoleBasedGuard>
              )
            }
          ]
        },

        {
          path: 'company',
          children: [
            { element: <Navigate to="/dashboard/company/list" replace />, index: true },
            {
              path: 'list',
              element: (
                <RoleBasedGuard permission="view_company">
                  <Compagnies />
                </RoleBasedGuard>
              )
            },
            {
              path: 'facturation',
              element: <Facturation />
            },
            {
              path: 'langue',
              element: <Langue />
            },
            {
              path: 'support',
              element: <Support />
            },
            {
              path: 'facturation/:factuId/pdf',
              element: <FacturationPdf />
            },
            {
              path: 'payment',
              element: (
                <RoleBasedGuard permission="view_payment">
                  <CompanyPayment />
                </RoleBasedGuard>
              )
            },
            {
              path: 'payment/new',
              element: (
                <RoleBasedGuard permission="add_payment">
                  <CompanyPaymentCreate />
                </RoleBasedGuard>
              )
            },
            {
              path: 'companyStatus',
              element: (
                <RoleBasedGuard permission="add_payment">
                  <CompanyStatus />
                </RoleBasedGuard>
              )
            },
            {
              path: 'payment/:paymentId/edit',
              element: (
                <RoleBasedGuard permission="change_payment">
                  <CompanyPaymentCreate />
                </RoleBasedGuard>
              )
            },
            {
              path: 'new',
              element: (
                <RoleBasedGuard permission="add_company">
                  <CompagnieCreate />
                </RoleBasedGuard>
              )
            },
            {
              path: ':companyId/edit',
              element: (
                <RoleBasedGuard permission="change_company">
                  <CompagnieCreate />
                </RoleBasedGuard>
              )
            }
          ]
        },
        {
          path: 'generale',
          children: [
            { element: <Navigate to="/dashboard/generale/list" replace />, index: true },
            {
              path: 'list',
              element: (
                <RoleBasedGuard permission="view_generale">
                  <Generale />
                </RoleBasedGuard>
              )
            },
            {
              path: 'new',
              element: (
                <RoleBasedGuard permission="add_generale">
                  <GeneraleCreate />
                </RoleBasedGuard>
              )
            },
            {
              path: ':generaleId/edit',
              element: (
                <RoleBasedGuard permission="change_generale">
                  <GeneraleCreate />
                </RoleBasedGuard>
              )
            }
          ]
        },

        {
          path: 'tchala',
          children: [
            { element: <Navigate to="/dashboard/tchala/list" replace />, index: true },
            {
              path: 'list',
              element: (
                // <RoleBasedGuard permission="view_tchala">
                  <TchalaAdminList />
                // </RoleBasedGuard>
              )
            },
            {
              path: 'new',
              element: <TchalaAdminCreate />
            },
           
            
          ]
        },

        {
          path: 'surcussale',
          children: [
            { element: <Navigate to="/dashboard/surcussale/list" replace />, index: true },
            {
              path: 'list',
              element: (
                <RoleBasedGuard permission="view_surcussale">
                  <Surcussale />
                </RoleBasedGuard>
              )
            },
            {
              path: 'new',
              element: (
                <RoleBasedGuard permission="add_surcussale">
                  <SurcussaleCreate />
                </RoleBasedGuard>
              )
            },
            {
              path: ':surcussaleId/edit',
              element: (
                <RoleBasedGuard permission="change_surcussale">
                  <SurcussaleCreate />
                </RoleBasedGuard>
              )
            }
          ]
        }
      ]
    },

    // Main Routes
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [{ path: '*', element: <Navigate to="/404" replace /> }]
    },
    {
      path: '/',
      element: <MainLayout />,
      children: [
        {
          element: (
            <GuestGuard>
              <HomePage />
            </GuestGuard>
          ),
          index: true
        },
        { path: 'tchala', element: <TchalaPage /> },
        { path: 'about-us', element: <About /> },
        { path: 'contact-us', element: <Contact /> },
        { path: 'faqs', element: <Faqs /> }
      ]
    },
    { path: '*', element: <Navigate to="/404" replace /> },
    {
      path: 'fiche-view/:refCode',
      element: <FicheDetail />
    }
  ]);
}

// IMPORT COMPONENTS

// Authentication
const Login = Loadable(lazy(() => import('../pages/authentication/Login')));
// Dashboard
const GeneralApp = Loadable(lazy(() => import('../pages/dashboard/GeneralApp')));
const UserAccount = Loadable(lazy(() => import('../pages/dashboard/UserAccount')));
const Agent = Loadable(lazy(() => import('../pages/dashboard/Agents')));
const AgentCreate = Loadable(lazy(() => import('../pages/dashboard/AgentCreate')));
const GenerateAgent = Loadable(lazy(() => import('../pages/dashboard/AgentGenerate')));
const GenerateCompany = Loadable(lazy(() => import('../pages/dashboard/CompanyGenerate')));
const GenerateTirage =Loadable(lazy(() => import('../pages/dashboard/TirageGenerate')));
const Supervisor = Loadable(lazy(() => import('../pages/dashboard/Supervisors')));
const Compagnies = Loadable(lazy(() => import('../pages/dashboard/Compagnies')));
const CompagnieCreate = Loadable(lazy(() => import('../pages/dashboard/CompagnieCreate')));
const CompanyPayment = Loadable(lazy(() => import('../pages/dashboard/CompanyPayment')));
const CompanyPaymentCreate = Loadable(lazy(() => import('../pages/dashboard/CompanyPaymentCreate')));
const CompanyStatus = Loadable(lazy(() => import('../pages/dashboard/CompanyPaymentSatus')));

const Generale = Loadable(lazy(() => import('../pages/dashboard/Generale')));
const GeneraleCreate = Loadable(lazy(() => import('../pages/dashboard/GeneraleCreate')));
const AppPromote = Loadable(lazy(() => import('../pages/dashboard/AppPromote')));
const AppPromoteCreate = Loadable(lazy(() => import('../pages/dashboard/AppPromoteCreate')));
const Pays = Loadable(lazy(() => import('../pages/dashboard/Pays')));
const PaysCreate = Loadable(lazy(() => import('../pages/dashboard/PaysCreate')));
const SupervisorCreate = Loadable(lazy(() => import('../pages/dashboard/SupervisorCreate')));
const Lotto = Loadable(lazy(() => import('../pages/dashboard/Lottos')));
const LottoCreate = Loadable(lazy(() => import('../pages/dashboard/LottoCreate')));
const Surcussale = Loadable(lazy(() => import('../pages/dashboard/Surcussales')));
const SurcussaleCreate = Loadable(lazy(() => import('../pages/dashboard/SurcussaleCreate')));
const Tirage = Loadable(lazy(() => import('../pages/dashboard/Tirages')));
const TirageCreate = Loadable(lazy(() => import('../pages/dashboard/TirageCreate')));
const TirageAuto = Loadable(lazy(() => import('../pages/dashboard/TirageAuto')));
const RemoveTirageAuto = Loadable(lazy(() => import('../pages/dashboard/RemoveTirageAuto')));
const Rapport = Loadable(lazy(() => import('../pages/dashboard/Rapports/RapportInit')));
const RapportJournalier = Loadable(lazy(() => import('../pages/dashboard/RapportJournalier')));
const RapportAvantVente = Loadable(lazy(() => import('../pages/dashboard/RapportsAvanTirage/RapportInit')));
const RapportApresVente = Loadable(lazy(() => import('../pages/dashboard/RapportsTirage/RapportInit')));
const RapportAgent = Loadable(lazy(() => import('../pages/dashboard/RapportAgents/RapportInit')));
const Facturation = Loadable(lazy(() => import('../pages/dashboard/Facturation')));
const Langue = Loadable(lazy(() => import('../pages/dashboard/Langue')));

const Support = Loadable(lazy(() => import('../pages/dashboard/ChatSupport')));

const FacturationPdf = Loadable(lazy(() => import('../pages/dashboard/FacturationPdf')));
const Boule = Loadable(lazy(() => import('../pages/dashboard/Boules')));
const BouleCreate = Loadable(lazy(() => import('../pages/dashboard/BouleCreate')));
const BouleDelCreate = Loadable(lazy(() => import('../pages/dashboard/BouleDelCreate')));
const InfoTirage = Loadable(lazy(() => import('../pages/dashboard/InfoTirages')));
const InfoTirageCreate = Loadable(lazy(() => import('../pages/dashboard/InfoTirageCreate')));
const Transaction = Loadable(lazy(() => import('../pages/dashboard/Transaction')));
const TransactionCreate = Loadable(lazy(() => import('../pages/dashboard/TransactionCreate')));
const TypeGame = Loadable(lazy(() => import('../pages/dashboard/TypeGame')));
const TypeGameCreate = Loadable(lazy(() => import('../pages/dashboard/TypeGameCreate')));
const TypeBoule = Loadable(lazy(() => import('../pages/dashboard/TypeBoule')));
const TypeBouleCreate = Loadable(lazy(() => import('../pages/dashboard/TypeBouleCreate')));
const IntervalleOption = Loadable(lazy(() => import('../pages/dashboard/IntervalleOption')));
const IntervalleOptionCreate = Loadable(lazy(() => import('../pages/dashboard/IntervalleOptionCreate')));
const TypeGameAgent = Loadable(lazy(() => import('../pages/dashboard/TypeGameAgent')));

const TypeGameAgentCreate = Loadable(lazy(() => import('../pages/dashboard/TypeGameAgentCreate')));


const TypeGameSurcussale = Loadable(lazy(() => import('../pages/dashboard/TypeGameSurcussale')));

const TypeGameSurcussaleCreate = Loadable(lazy(() => import('../pages/dashboard/TypeGameSurcussaleCreate')));


const TypeBouleAgent = Loadable(lazy(() => import('../pages/dashboard/TypeBouleAgent')));
const TypeBouleAgentCreate = Loadable(lazy(() => import('../pages/dashboard/TypeBouleAgentCreate')));
const LimitAllBouleCreate = Loadable(lazy(() => import('../pages/dashboard/LimitAllBouleCreate')));
const LimitAllBoule = Loadable(lazy(() => import('../pages/dashboard/LimitAllBoule')));
const LimiteGrappeCreate = Loadable(lazy(()=> import('../pages/dashboard/LimitGrappeCreate')))
const LimiteGrappe = Loadable(lazy(()=> import('../pages/dashboard/LimitGrappe')));
const LimitBoulePaireCreate = Loadable(lazy(() => import('../pages/dashboard/LimitBoulePaireCreate')));
const LimitBoulePaire = Loadable(lazy(() => import('../pages/dashboard/LimitBoulePaire')));
const LimitBoulePointCreate = Loadable(lazy(() => import('../pages/dashboard/LimitBoulePointCreate')));
const LimitBoulePoint = Loadable(lazy(() => import('../pages/dashboard/LimitBoulePoint')));
const Prime = Loadable(lazy(() => import('../pages/dashboard/Prime')));
const TirageConfCreate = Loadable(lazy(() => import('../pages/dashboard/TirageConfCreate')));
const PrimeGenerale = Loadable(lazy(() => import('../pages/dashboard/PrimeGenerale')));
const TirageConfCreateModif = Loadable(lazy(() => import('../pages/dashboard/TirageConfCreateModif')));
const Statistique = Loadable(lazy(() => import('../pages/dashboard/Statistiques')));
const TracabiliteAgent = Loadable(lazy(() => import('../pages/dashboard/TracabiliteAgents')));
const TracabiliteSupervisor = Loadable(lazy(() => import('../pages/dashboard/TracabiliteSupervisors')));
const TracabiliteCompany = Loadable(lazy(() => import('../pages/dashboard/TracabiliteCompany')));
const Fiche = Loadable(lazy(() => import('../pages/dashboard/Fiches')));

const FicheDetail = Loadable(lazy(() => import('../pages/dashboard/FicheDetail')));
const FicheGagnant = Loadable(lazy(() => import('../pages/dashboard/FicheGagnants')));
const FicheSupp = Loadable(lazy(() => import('../pages/dashboard/FicheSupp')));
const NotFound = Loadable(lazy(() => import('../pages/Page404')));
// Main
const HomePage = Loadable(lazy(() => import('../pages/LandingPage')));
const About = Loadable(lazy(() => import('../pages/About')));
const Contact = Loadable(lazy(() => import('../pages/Contact')));
const Faqs = Loadable(lazy(() => import('../pages/Faqs')));
const ComingSoon = Loadable(lazy(() => import('../pages/ComingSoon')));
const Maintenance = Loadable(lazy(() => import('../pages/Maintenance')));
const Page500 = Loadable(lazy(() => import('../pages/Page500')));

const MariageCreate=Loadable(lazy(() => import('../pages/dashboard/MariageAgentCreate')));
const MariageAgent=Loadable(lazy(() => import('../pages/dashboard/MariageAgent')));


const MariageSurCreate=Loadable(lazy(() => import('../pages/dashboard/MariageSurCreate')));
const MariageSurcussale=Loadable(lazy(() => import('../pages/dashboard/MariageSurcussale')));


const AmountMariageSurcussale=Loadable(lazy(() => import('../pages/dashboard/AmountMariageSurcussale')));
const MariageSurAMountCreate=Loadable(lazy(() => import('../pages/dashboard/MariageSurAMountCreate')));


const AmountMariageAgent=Loadable(lazy(() => import('../pages/dashboard/AmountMariageAgent')));
const MariageAgentAMountCreate=Loadable(lazy(() => import('../pages/dashboard/MariageAgentAMountCreate')));



// TCHALA
const TchalaPage = Loadable(lazy(() => import('../pages/TchalaPage')));
const TchalaAdminList = Loadable(lazy(() => import('../pages/dashboard/TchalaAdminList')));
const TchalaAdminCreate= Loadable(lazy(() => import('../pages/dashboard/TchalaAdminCreate')));

const TirageStatus2 = Loadable(lazy(() => import('../pages/dashboard/TiragesStatus2')));


const TirageStatus = Loadable(lazy(() => import('../pages/dashboard/TiragesStatus')));
const DeleteFicheRange = Loadable(lazy(() => import('../pages/dashboard/DeleteFicheRange')));
